/**
 * InstallPage — מדריך התקנת האפליקציה למסך הבית
 *
 * A public, linkable page (/install) that יפתח can send by WhatsApp/SMS. It
 * does three things, in order of how much the user has to work:
 *
 *   1. On Android Chrome we can install in ONE TAP via beforeinstallprompt —
 *      no instructions needed, just a button.
 *   2. If the link was opened inside WhatsApp/Facebook's built-in browser,
 *      installing is impossible. We say so first, because otherwise the user
 *      follows five steps that cannot work.
 *   3. Otherwise: platform-tabbed, illustrated, step-by-step instructions,
 *      plus the same steps as plain text (easier to read aloud on the phone,
 *      and the version a screen reader gets).
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Smartphone,
  Apple,
  Check,
  ChevronRight,
  ChevronLeft,
  Download,
  AlertTriangle,
  Copy,
  ExternalLink,
} from 'lucide-react'
import usePwaInstall from '../hooks/usePwaInstall'
import PhoneMockup, { type StepScreen } from '../components/install/PhoneMockup'

type GuideTab = 'android' | 'ios'

interface Step {
  title: string
  body: string
  screen: StepScreen
}

const ANDROID_STEPS: Step[] = [
  {
    title: 'פתחו את האתר בדפדפן Chrome',
    body: 'ודאו שהאתר פתוח בדפדפן Chrome של הטלפון — ולא מתוך וואטסאפ, פייסבוק או אינסטגרם. אם הגעתם לכאן מקישור בוואטסאפ, הקישו על שלוש הנקודות למעלה ובחרו "פתח בדפדפן".',
    screen: 'android-open',
  },
  {
    title: 'הקישו על שלוש הנקודות ⋮',
    body: 'שלוש הנקודות נמצאות בפינה העליונה של הדפדפן, לצד שורת הכתובת.',
    screen: 'android-menu-button',
  },
  {
    title: 'בחרו "התקנת אפליקציה"',
    body: 'בתפריט שנפתח בחרו "התקנת אפליקציה". בחלק מהמכשירים האפשרות נקראת "הוספה למסך הבית".',
    screen: 'android-menu',
  },
  {
    title: 'אשרו את ההתקנה',
    body: 'ייפתח חלון אישור עם שם האפליקציה — הקישו "התקן" (או "הוסף").',
    screen: 'android-confirm',
  },
  {
    title: 'סיימתם — האייקון במסך הבית',
    body: 'האפליקציה מופיעה עכשיו במסך הבית עם שאר האפליקציות, ונפתחת במסך מלא בלי שורת הכתובת של הדפדפן.',
    screen: 'home-screen',
  },
]

const IOS_STEPS: Step[] = [
  {
    title: 'פתחו את האתר בדפדפן Safari',
    body: 'באייפון ההוספה למסך הבית עובדת מדפדפן Safari. אם הגעתם מקישור בוואטסאפ, הקישו על אייקון השיתוף בתחתית המסך ובחרו "פתח ב-Safari".',
    screen: 'ios-open',
  },
  {
    title: 'הקישו על כפתור השיתוף',
    body: 'הכפתור הוא ריבוע עם חץ שיוצא ממנו כלפי מעלה, בסרגל התחתון של Safari (במכשירים ישנים — בסרגל העליון).',
    screen: 'ios-share-button',
  },
  {
    title: 'בחרו "הוספה למסך הבית"',
    body: 'גללו מעט ברשימה שנפתחה ובחרו "הוספה למסך הבית" (Add to Home Screen).',
    screen: 'ios-share-sheet',
  },
  {
    title: 'הקישו "הוסף"',
    body: 'אפשר לערוך את שם האפליקציה, ואז להקיש "הוסף" בפינה העליונה.',
    screen: 'ios-confirm',
  },
  {
    title: 'סיימתם — האייקון במסך הבית',
    body: 'האפליקציה מופיעה במסך הבית. בפתיחה הראשונה תתבקשו להתחבר פעם אחת — זו התחברות נפרדת מהדפדפן, ואחריה האפליקציה זוכרת אתכם.',
    screen: 'home-screen',
  },
]

export default function InstallPage() {
  const { isStandalone, wasInstalled, canPrompt, promptInstall, platform, isInAppBrowser } =
    usePwaInstall()

  const [tab, setTab] = useState<GuideTab>(platform === 'ios' ? 'ios' : 'android')
  const [stepIndex, setStepIndex] = useState(0)
  const [copied, setCopied] = useState(false)
  const tabRefs = useRef<Record<GuideTab, HTMLButtonElement | null>>({ android: null, ios: null })

  const steps = tab === 'ios' ? IOS_STEPS : ANDROID_STEPS
  const step = steps[stepIndex]
  const host = typeof window !== 'undefined' ? window.location.host : 'app.example.co.il'

  // Moving between tabs restarts the guide — step 3 of Android means nothing on iOS.
  useEffect(() => {
    setStepIndex(0)
  }, [tab])

  const selectTab = (next: GuideTab) => {
    setTab(next)
    tabRefs.current[next]?.focus()
  }

  const onTabKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    selectTab(tab === 'android' ? 'ios' : 'android')
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white px-4 py-8">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 sm:text-3xl">
              התקנת האפליקציה של יפתח ונגר
            </h1>
            <p className="mt-1 text-gray-600">מדריך הוספה למסך הבית — אנדרואיד ואייפון</p>
          </div>
          <img
            src="/icons/icon-192.png"
            alt="אייקון האפליקציה"
            className="h-14 w-14 flex-shrink-0 rounded-2xl shadow-md"
          />
        </header>

        {/* Already installed */}
        {(isStandalone || wasInstalled) && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-4">
            <Check className="mt-0.5 flex-shrink-0 text-green-700" size={20} aria-hidden="true" />
            <div>
              <p className="font-semibold text-green-900">האפליקציה מותקנת 🎉</p>
              <p className="mt-1 text-sm text-green-800">
                אפשר לפתוח אותה מעכשיו ישירות ממסך הבית של הטלפון.
              </p>
              <Link
                to="/"
                className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-green-700 px-5 font-semibold text-white hover:bg-green-800"
              >
                כניסה לאזור האישי
                <ChevronLeft size={18} aria-hidden="true" />
              </Link>
            </div>
          </div>
        )}

        {/* In-app browser — the single most common reason installing "doesn't work" */}
        {isInAppBrowser && !isStandalone && (
          <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle
                className="mt-0.5 flex-shrink-0 text-amber-700"
                size={20}
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="font-semibold text-amber-900">
                  פתחתם את הקישור בתוך אפליקציה אחרת
                </p>
                <p className="mt-1 text-sm text-amber-900">
                  הדפדפן הפנימי של וואטסאפ/פייסבוק לא יודע להוסיף אפליקציות למסך הבית. העתיקו
                  את הקישור ופתחו אותו ב-Chrome (אנדרואיד) או ב-Safari (אייפון), ואז חזרו לכאן.
                </p>
                <button
                  type="button"
                  onClick={copyLink}
                  className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-amber-400 bg-white px-5 font-semibold text-amber-900 hover:bg-amber-100"
                >
                  <Copy size={18} aria-hidden="true" />
                  {copied ? 'הקישור הועתק!' : 'העתיקו את הקישור'}
                </button>
                <p aria-live="polite" className="sr-only">
                  {copied ? 'הקישור הועתק ללוח' : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Primary action */}
        {!isStandalone && (
          <div className="mb-8 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100 sm:p-6">
            {canPrompt ? (
              <>
                <button
                  type="button"
                  onClick={promptInstall}
                  className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-6 text-lg font-bold text-white transition-colors hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                >
                  <Download size={22} aria-hidden="true" />
                  התקינו את האפליקציה עכשיו
                </button>
                <p className="mt-2 text-center text-sm text-gray-600">
                  המכשיר שלכם תומך בהתקנה בלחיצה אחת — אין צורך במדריך.
                </p>
              </>
            ) : (
              <>
                <a
                  href="/"
                  className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-6 text-lg font-bold text-white transition-colors hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                >
                  <ExternalLink size={20} aria-hidden="true" />
                  פתחו את האזור האישי עכשיו
                </a>
                <p className="mt-2 text-center text-sm text-gray-600">
                  נפתח את {host} — התחברו, ואז הוסיפו למסך הבית לפי המדריך למטה.
                </p>
              </>
            )}
          </div>
        )}

        {/* Platform tabs */}
        <div
          role="tablist"
          aria-label="בחירת מדריך לפי סוג המכשיר"
          className="mb-4 grid grid-cols-2 gap-2"
        >
          {(
            [
              { id: 'android', label: 'מדריך אנדרואיד', icon: <Smartphone size={18} /> },
              { id: 'ios', label: 'מדריך אייפון', icon: <Apple size={18} /> },
            ] as const
          ).map((item) => (
            <button
              key={item.id}
              ref={(el) => {
                tabRefs.current[item.id] = el
              }}
              type="button"
              role="tab"
              id={`tab-${item.id}`}
              aria-selected={tab === item.id}
              aria-controls={`panel-${item.id}`}
              tabIndex={tab === item.id ? 0 : -1}
              onClick={() => selectTab(item.id)}
              onKeyDown={onTabKeyDown}
              className={`flex min-h-[48px] items-center justify-center gap-2 rounded-xl border px-4 font-semibold transition-colors ${
                tab === item.id
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>

        {/* Step viewer */}
        <section
          role="tabpanel"
          id={`panel-${tab}`}
          aria-labelledby={`tab-${tab}`}
          tabIndex={0}
          className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100 sm:p-6"
        >
          <div className="grid items-center gap-6 sm:grid-cols-2">
            <PhoneMockup screen={step.screen} host={host} />

            <div>
              <p className="text-sm font-semibold text-primary-700">
                שלב {stepIndex + 1} מתוך {steps.length}
              </p>
              <h2 className="mt-1 font-heading text-xl font-bold text-gray-900">{step.title}</h2>
              <p className="mt-2 leading-relaxed text-gray-700">{step.body}</p>

              {/* Announce the step change to screen readers */}
              <p aria-live="polite" className="sr-only">
                שלב {stepIndex + 1} מתוך {steps.length}: {step.title}. {step.body}
              </p>

              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                  disabled={stepIndex === 0}
                  className="flex min-h-[44px] items-center gap-1 rounded-xl border border-gray-300 px-4 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight size={18} aria-hidden="true" />
                  הקודם
                </button>
                <button
                  type="button"
                  onClick={() => setStepIndex((i) => Math.min(steps.length - 1, i + 1))}
                  disabled={stepIndex === steps.length - 1}
                  className="flex min-h-[44px] flex-1 items-center justify-center gap-1 rounded-xl bg-primary-600 px-4 font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  הבא
                  <ChevronLeft size={18} aria-hidden="true" />
                </button>
              </div>

              {/* Progress dots — decorative, the counter above carries the meaning */}
              <div aria-hidden="true" className="mt-4 flex justify-center gap-1.5">
                {steps.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === stepIndex ? 'w-6 bg-primary-600' : 'w-1.5 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* The same steps as plain text — easier to follow while holding the phone */}
          <details className="mt-6 rounded-xl bg-gray-50 p-4">
            <summary className="cursor-pointer font-semibold text-gray-800">
              כל השלבים ברשימה אחת
            </summary>
            <ol className="mt-3 list-decimal space-y-2 pr-5 text-gray-700">
              {steps.map((s) => (
                <li key={s.title}>
                  <span className="font-medium">{s.title}</span> — {s.body}
                </li>
              ))}
            </ol>
          </details>
        </section>

        {/* FAQ */}
        <section className="mt-8 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100 sm:p-6">
          <h2 className="font-heading text-xl font-bold text-gray-900">שאלות נפוצות</h2>
          <dl className="mt-4 space-y-4 text-gray-700">
            <div>
              <dt className="font-semibold text-gray-900">האם זו אפליקציה מחנות האפליקציות?</dt>
              <dd className="mt-1">
                לא. האזור האישי פועל דרך הדפדפן, וההוספה למסך הבית יוצרת אייקון שפותח אותו במסך
                מלא — בדיוק כמו אפליקציה. אין צורך ב-Google Play או App Store.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">כמה מקום זה תופס בטלפון?</dt>
              <dd className="mt-1">
                כמעט כלום — מדובר בקיצור דרך, לא בהורדה של אפליקציה.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">אצטרך להתחבר שוב?</dt>
              <dd className="mt-1">
                בפתיחה הראשונה מהאייקון החדש — כן, פעם אחת. באייפון במיוחד, האפליקציה המותקנת
                נפרדת מהדפדפן ולכן מבקשת התחברות מחדש. אחר כך היא זוכרת אתכם.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-900">איך מסירים את האייקון?</dt>
              <dd className="mt-1">
                לחיצה ארוכה על האייקון במסך הבית ובחירה ב"הסרה" / "מחיקה" — בדיוק כמו כל
                אפליקציה אחרת. החשבון והנתונים שלכם נשארים כמובן.
              </dd>
            </div>
          </dl>
        </section>

        <p className="mt-6 text-center text-sm text-gray-600">
          נתקלתם בקושי?{' '}
          <Link to="/login" className="text-primary-700 underline hover:text-primary-800">
            כניסה לאזור האישי
          </Link>{' '}
          ואפשר לפנות אלינו דרך הצ'אט.
        </p>
      </div>
    </div>
  )
}
